/**
 * Builds the line item discount label shown on the Direct Web cart.
 *
 * Trade agreement discounts arrive as a formula string on the product
 * (discount_formula): "10%" (percentage), "5" (absolute, per unit) or a
 * multi-level / custom chain such as "5+2.5%+1". Each step is shown in the
 * order it is applied — "-RM 5.00 -2.50% -RM 1.00" — instead of collapsing the
 * whole chain into one effective percentage.
 *
 * Tokenising mirrors CartItem.calculateComplexDiscount (models/carts.js), which
 * computes the line total, so the label always describes the same discount the
 * cart charges: "+" (or no operator) is a discount, "-" is a markup, and a
 * number directly after a "%" is ignored.
 *
 * Display only — nothing here feeds pricing or the order payload.
 */
function parseDiscountFormula(formula) {
    if (formula === null || formula === undefined || String(formula).trim() === "") {
        return [];
    }

    const tokens = String(formula).replace(/\s+/g, "").split(/([\+\-\%])/);
    const steps = [];
    let operator = "+";

    tokens.forEach((token, key) => {
        if (!isNaN(token) && token !== "") {
            if (operator !== "%") {
                steps.push({
                    type: key + 1 < tokens.length && tokens[key + 1] === "%" ? "percentage" : "amount",
                    value: Number(token),
                    markup: operator === "-",
                });
            }
        } else {
            operator = token;
        }
    });

    return steps;
}

// Line items always display at 2dp, matching the DB precision - not siteCurrencyDecimalPlaces, which is 0
// for TWD and is reserved for the cart/document-level total.
const LINE_ITEM_DECIMAL_PLACES = 2;

function formatDiscountStep(step, currencySymbol) {
    const sign = step.markup ? "+" : "-";
    const value = Math.abs(step.value);

    if (step.type === "percentage") {
        return `${sign}${value.toFixed(2)}%`;
    }

    const amount = value.toLocaleString("en-US", {
        minimumFractionDigits: LINE_ITEM_DECIMAL_PLACES,
        maximumFractionDigits: LINE_ITEM_DECIMAL_PLACES,
    });
    return `${sign}${currencySymbol} ${amount}`;
}

/**
 * @param {Object} options
 * @param {?string} options.formula        trade agreement discount_formula, or null
 * @param {number}  options.unitPrice      price after discount (fallback only)
 * @param {number}  options.regularPrice   price before discount (fallback only)
 * @param {string}  options.currencySymbol e.g. "RM"
 * @returns {string[]} one label per discount level, in applied order; empty when there is no discount
 */
function formatLineDiscountSteps({ formula, unitPrice, regularPrice, currencySymbol = "RM" }) {
    const steps = parseDiscountFormula(formula).filter((step) => step.value !== 0);
    if (steps.length > 0) {
        return steps.map((step) => formatDiscountStep(step, currencySymbol));
    }

    // No formula (sale price, coupon, bundle): fall back to the effective percentage.
    const percentage = (1 - unitPrice / regularPrice) * 100;
    if (!isFinite(percentage) || Number(percentage.toFixed(2)) === 0) {
        return [];
    }

    return [formatDiscountStep({ type: "percentage", value: percentage, markup: percentage < 0 }, currencySymbol)];
}

// Applies levels in order, each percentage on the running price - as TradeAgreementService::calculateComplex does.
function applyDiscountLevels(price, levels) {
    return levels.reduce((result, level) => {
        const value = level.type === "percentage" ? (result * level.value) / 100 : level.value;
        return level.markup ? result + value : result - value;
    }, price);
}

function bundleChildDiscountLevels(child) {
    const component = child.component || {};
    if (child.line_item || child.isFreeQuantity || (component.price !== null && component.price !== undefined)) {
        return null;
    }

    const levels = parseDiscountFormula(child.product?.getDiscountFormula?.());
    // The product price must come from its formula alone; a sale price set any other way is not in the levels.
    const regularPrice = Number(child.product?.getOriginalPrice?.());
    const productPrice = Number(child.product?.getCurrentPrice?.());
    if (Math.abs(applyDiscountLevels(regularPrice, levels) - productPrice) > 0.005) {
        return null;
    }

    if (Number(component.discount_percentage)) {
        levels.push({ type: "percentage", value: Number(component.discount_percentage), markup: false });
    } else if (Number(component.discount_amount)) {
        levels.push({ type: "amount", value: Number(component.discount_amount), markup: false });
    }

    return levels.filter((level) => level.value !== 0);
}

/** Discount levels for one bundle component (its per-unit price). */
function bundleChildDiscountSteps(child, currencySymbol) {
    const levels = bundleChildDiscountLevels(child);
    if (levels && levels.length > 0) {
        return levels.map((level) => formatDiscountStep(level, currencySymbol));
    }

    return formatLineDiscountSteps({
        formula: null,
        unitPrice: child.getPrice(),
        regularPrice: child.getOriginalPrice(),
        currencySymbol,
    });
}

/** Discount levels for a cart item (bundles have no formula of their own and use the fallback). */
function cartItemDiscountSteps(item, currencySymbol) {
    // Component rows (cart table view): the product formula alone would drop the component's own discount.
    if (item._type == "child_bundle_cart_item") {
        return bundleChildDiscountSteps(item, currencySymbol);
    }

    return formatLineDiscountSteps({
        formula: item._type == "bundle_cart_item" ? null : item.product.getDiscountFormula(),
        unitPrice: item.getDisplayUnitPrice(),
        regularPrice: item.getDisplayRegularPrice(),
        currencySymbol,
    });
}

/**
 * Discount levels saved on a document line item (invoice, proforma invoice): its `discounts`
 * list of { type: "percentage" | "unit", value, level }, in level order. A "unit" discount is
 * an amount per unit. Lines without saved discounts fall back to the effective percentage
 * between subtotal and total.
 */
function documentLineDiscountSteps(lineItem, currencySymbol) {
    // Object.values: the saved collection may serialise as an array or keyed by type.
    const steps = Object.values(lineItem.discounts || {})
        .filter((discount) => Number(discount.value) > 0)
        .sort((a, b) => (a.level ?? 0) - (b.level ?? 0))
        .map((discount) =>
            formatDiscountStep(
                {
                    type: discount.type === "percentage" ? "percentage" : "amount",
                    value: Number(discount.value),
                    markup: false,
                },
                currencySymbol
            )
        );
    if (steps.length > 0) {
        return steps;
    }

    return formatLineDiscountSteps({
        formula: null,
        unitPrice: Number(lineItem.total),
        regularPrice: Number(lineItem.subtotal),
    });
}

module.exports = { parseDiscountFormula, formatLineDiscountSteps, cartItemDiscountSteps, documentLineDiscountSteps };
